import type { InvoicingSettings } from '../../../hooks/useGetSettings';
import { resolveUnmatchedLineItemsNexSubmitMode } from '../../../utils/unmatchedLineItemsNexSubmitMode';

type SettingsSection = { key?: string; data?: unknown };

export function findInvoicingSettingsSection(
    sections?: SettingsSection[],
): { data?: Record<string, unknown> } | undefined {
    return sections?.find((s) => s?.key === 'invoicing') as { data?: Record<string, unknown> } | undefined;
}

export function readInvoicingSettingsFromSections(
    topSections?: SettingsSection[],
    bottomSections?: SettingsSection[],
): InvoicingSettings {
    const section =
        findInvoicingSettingsSection(topSections) ?? findInvoicingSettingsSection(bottomSections);
    const data = section?.data as InvoicingSettings | undefined;
    return {
        freightAsLineItem: data?.freightAsLineItem ?? true,
        salesTaxAsLineItem: data?.salesTaxAsLineItem === true,
        vendorNexAliases: data?.vendorNexAliases ?? [],
        vendorNexAliasMappings: data?.vendorNexAliasMappings ?? [],
        flaggedSpecialVendors: data?.flaggedSpecialVendors ?? [],
        vendorFlaggingRules: data?.vendorFlaggingRules ?? [],
        receivedAmountMismatchPercentThreshold: data?.receivedAmountMismatchPercentThreshold ?? 0.5,
        returnedAmountMismatchPercentThreshold: data?.returnedAmountMismatchPercentThreshold ?? 0.5,
        receivedAmountMismatchBaseDollarThreshold: data?.receivedAmountMismatchBaseDollarThreshold ?? 50,
        returnedAmountMismatchBaseDollarThreshold: data?.returnedAmountMismatchBaseDollarThreshold ?? 50,
        receivedAmountMismatchDollarThreshold:
            data?.receivedAmountMismatchDollarThreshold ??
            data?.receivedAmountMismatchBaseDollarThreshold ??
            50,
        returnedAmountMismatchDollarThreshold:
            data?.returnedAmountMismatchDollarThreshold ??
            data?.returnedAmountMismatchBaseDollarThreshold ??
            50,
        amountMismatchFlaggingEnabled: data?.amountMismatchFlaggingEnabled ?? true,
        amountMismatchFlagBothDirections: data?.amountMismatchFlagBothDirections !== false,
        quantityMismatchFlaggingEnabled: data?.quantityMismatchFlaggingEnabled ?? true,
        priceMismatchFlaggingEnabled: data?.priceMismatchFlaggingEnabled ?? true,
        nexLineQuantityBasis: data?.nexLineQuantityBasis === 'qtyOnOrder' ? 'qtyOnOrder' : 'qtyRec',
        nexReconciliationIdentifierMode: data?.nexReconciliationIdentifierMode ?? 'ro',
        locationDefaultTaxRates: data?.locationDefaultTaxRates ?? [],
        unmatchedLineItemsNexSubmitMode: resolveUnmatchedLineItemsNexSubmitMode(data),
    };
}

export function formatInvoiceDetailMoney(value: number | undefined | null): string {
    const n = typeof value === 'number' ? value : 0;
    const sign = n < 0 ? '-' : '';
    return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
